// ETJump trickjump lines (etj_trickjump_lines.cpp): a recorded route is a list
// of trails, one per jump; a point is added every 50 ms at the player's feet
// with its horizontal speed, and pressing jump starts a new trail. Drawn as a
// ribbon coloured by speed (blue slow .. red fast) with markers at the jumps.

import type { Vec3 } from '../math';

export interface TjlNode {
  pos: Vec3;
  speed: number;
}

export interface TjlRoute {
  name: string;
  trails: TjlNode[][];
}

/** Points per second: one every 50 ms. */
const ADD_INTERVAL = 50;

export class TjlRecorder {
  private trails: TjlNode[][] = [];
  private current: TjlNode[] = [];
  private jumpReleased = true;
  private nextAdd = -Infinity;

  constructor(readonly name: string) {}

  /** TrickjumpLines::addPosition, once per move. */
  add(origin: Vec3, velocity: Vec3, jumpHeld: boolean, time: number): void {
    if (jumpHeld && this.jumpReleased) {
      // a jump: the trail so far is done, the next one starts at its last point
      this.jumpReleased = false;
      if (this.current.length) {
        this.trails.push(this.current);
        this.current = [this.current[this.current.length - 1]];
      }
    } else if (!jumpHeld) this.jumpReleased = true;
    if (time > this.nextAdd) {
      this.current.push({ pos: [origin[0], origin[1], origin[2] - 24], speed: Math.hypot(velocity[0], velocity[1]) });
      this.nextAdd = time + ADD_INTERVAL;
    }
  }

  /** TrickjumpLines::stopRecord */
  finish(): TjlRoute {
    const trails = [...this.trails, this.current].filter((t) => t.length > 0);
    return { name: this.name, trails };
  }
}

/** Speed range of a route (for the colours). */
export function speedRange(route: TjlRoute): { min: number; max: number } {
  let min = Infinity;
  let max = 0;
  for (const t of route.trails) for (const n of t) {
    min = Math.min(min, n.speed);
    max = Math.max(max, n.speed);
  }
  return { min: Number.isFinite(min) ? min : 0, max };
}

/** computeColorForNode: speed -> hue 300 (slow, blue/magenta) .. 0 (fast, red), as RGB 0..1. */
export function speedColor(speed: number, min: number, max: number): [number, number, number] {
  const norm = max > min ? (speed - min) / (max - min) : 1;
  const hmax = 300;
  const h = Math.abs(Math.pow(Math.max(0, norm), 1.35) * hmax - hmax);
  // hsv2rgb with s = v = 1 (sector, and x as in the C++: |sector % 2 - 1| is 0 or 1)
  const sector = Math.min(5, Math.trunc(h / 60));
  const x = 1 - Math.abs((sector % 2) - 1);
  const table: [number, number, number][] = [
    [1, x, 0],
    [x, 1, 0],
    [0, 1, x],
    [0, x, 1],
    [x, 0, 1],
    [1, 0, x],
  ];
  return table[sector];
}

/** A route as JSON for storage; null for anything that is not one. */
export function parseRoute(text: string | null): TjlRoute | null {
  if (!text) return null;
  try {
    const r = JSON.parse(text) as TjlRoute;
    if (!r || typeof r.name !== 'string' || !Array.isArray(r.trails)) return null;
    for (const t of r.trails) for (const n of t) if (!Array.isArray(n.pos) || n.pos.length !== 3 || typeof n.speed !== 'number') return null;
    return r;
  } catch {
    return null;
  }
}
